import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateFramePromptsFromDescription, generateSceneImage, preloadProjectAssets, transcribeShotNarration } from '../services/geminiService';
import { CREDIT_COSTS } from '../types';
import { ImageEditorModal } from './ImageEditorModal';
import { CharacterPortraitModal } from './CharacterPortraitModal';
import { VideoPreProductionModal } from './VideoPreProductionModal';
import { AnimateVideoModal } from './AnimateVideoModal';
import { VideoEditorModal } from './VideoEditorModal';
import { VideoPreviewModal } from './VideoPreviewModal';
import { ShotAudioModal } from './ShotAudioModal';
import AIToolkitSelector from './AIToolkitSelector';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { ArrowLeftIcon } from './icons/ArrowLeftIcon';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { FilmIcon } from './icons/FilmIcon';
import { PhotoIcon } from './icons/PhotoIcon';
import { PencilIcon } from './icons/PencilIcon';
import { UploadIcon } from './icons/UploadIcon';
import { TrashIcon } from './icons/TrashIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { PlayIcon } from './icons/PlayIcon';
import { PlusIcon } from './icons/PlusIcon';
import { EyeIcon } from './icons/EyeIcon';
import { CloseIcon } from './icons/CloseIcon';

export const Storyboard = ({ projectData, onSubmit, onBack, onProjectUpdate, currentCredits, onDeductCredits, onTrackCreditsUsed, onSaveMedia }) => {
    const [scenes, setScenes] = useState(projectData.scenes || []);
    
    const scenesRef = useRef(scenes);
    useEffect(() => {
        scenesRef.current = scenes;
    }, [scenes]);

    const [editingInfo, setEditingInfo] = useState(null);
    const [editingCharacter, setEditingCharacter] = useState(null);
    const [preppingVideoForShot, setPreppingVideoForShot] = useState(null);
    const [previewingVideoUrl, setPreviewingVideoUrl] = useState(null);
    const [managingAudioForShot, setManagingAudioForShot] = useState(null);
    const [animatingShot, setAnimatingShot] = useState(null);
    const [editingVideoForShot, setEditingVideoForShot] = useState(null);
    const [updatingPromptsForShotId, setUpdatingPromptsForShotId] = useState(null);
    const [generatingFrames, setGeneratingFrames] = useState({});
    const [generatingScenes, setGeneratingScenes] = useState(new Set());
    const [generatingVideosForScenes, setGeneratingVideosForScenes] = useState(new Set());
    const [sceneErrors, setSceneErrors] = useState({});
    const [isGeneratingAll, setIsGeneratingAll] = useState(false);
    const [isGeneratingAllVideos, setIsGeneratingAllVideos] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, message: '' });
    const [editingCharacterForShot, setEditingCharacterForShot] = useState(null);
    const [videoGenerationProgress, setVideoGenerationProgress] = useState({});
    const [viewingFrameImage, setViewingFrameImage] = useState(null); // { url: string, title: string }
    const [showAIToolkitModal, setShowAIToolkitModal] = useState(false); // AI Toolkit modal state
    const videoInputRefs = useRef({});
    const frameInputRefs = useRef({});
    const debounceTimeoutRef = useRef(null);

    const selectedModelName = projectData.modelPreferences?.video?.general;
    const requiresStartFrame = true;
    const requiresEndFrame = selectedModelName?.toLowerCase() === 'veo 3.1';

    const syncToParent = (updatedScenes) => {
        console.log('🔄 syncToParent called with', updatedScenes.length, 'scenes');
        // Log image status for each shot
        updatedScenes.forEach((scene, sIdx) => {
            scene.shots.forEach((shot, shotIdx) => {
                if (shot.startFrameImage || shot.endFrameImage) {
                    console.log(`  Scene ${sIdx + 1}, Shot ${shotIdx + 1}: startFrame=${shot.startFrameImage ? 'YES' : 'NO'}, endFrame=${shot.endFrameImage ? 'YES' : 'NO'}`);
                }
            });
        });
        setScenes(updatedScenes);
        onProjectUpdate({ ...projectData, scenes: updatedScenes });
    };

    const handleShotUpdate = (sceneId, shotId, field, value) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                const shotIndex = scene.shots.findIndex(s => s.id === shotId);
                
                return {
                    ...scene,
                    shots: scene.shots.map((shot, idx) => {
                        if (shot.id === shotId) {
                            return { ...shot, [field]: value };
                        }
                        
                        // CONTINUITY RULE: When end frame of shot N changes, 
                        // automatically update start frame of shot N+1
                        if (field === 'endFrameImage' && idx === shotIndex + 1) {
                            console.log(`🔗 Continuity: Propagating end frame from Shot ${shotIndex + 1} to start frame of Shot ${idx + 1}`);
                            return { ...shot, startFrameImage: value };
                        }
                        
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    // Helper to update multiple fields on a shot atomically (avoid race conditions)
    const handleShotUpdateMultiple = (sceneId, shotId, updates) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map((shot) => {
                        if (shot.id === shotId) {
                            return { ...shot, ...updates };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        console.log(`📹 Updated shot ${shotId} with:`, Object.keys(updates).join(', '));
        syncToParent(newScenes);
    };

    const updateFramePrompts = async (sceneId, shotId, newDescription) => {
        if (!newDescription.trim()) {
            setUpdatingPromptsForShotId(null);
            return;
        }
        setUpdatingPromptsForShotId(shotId);
        try {
            const { startFramePrompt, endFramePrompt } = await generateFramePromptsFromDescription(newDescription);
            
            setScenes(prevScenes => {
                const newScenes = prevScenes.map(scene => {
                    if (scene.id === sceneId) {
                        return {
                            ...scene,
                            shots: scene.shots.map(shot => {
                                if (shot.id === shotId) {
                                    return { ...shot, startFramePrompt, endFramePrompt };
                                }
                                return shot;
                            })
                        };
                    }
                    return scene;
                });
                
                setTimeout(() => onProjectUpdate({ ...projectData, scenes: newScenes }), 0);
                return newScenes;
            });
        } catch (err) {
            console.error("Failed to generate prompts:", err);
        } finally {
            setUpdatingPromptsForShotId(null);
        }
    };

    const handleDescriptionChange = (sceneId, shotId, newDescription) => {
        handleShotUpdate(sceneId, shotId, 'description', newDescription);
        
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        debounceTimeoutRef.current = setTimeout(() => {
            updateFramePrompts(sceneId, shotId, newDescription);
        }, 1000);
    };

    const handleAddScene = () => {
        const newScene = {
            id: uuidv4(),
            shots: [
                {
                    id: uuidv4(),
                    description: '',
                    characterIds: [],
                    elementIds: []
                }
            ]
        };
        syncToParent([...scenes, newScene]);
    };

    const handleAddShot = (sceneId) => {
        const newShot = {
            id: uuidv4(),
            description: '',
            characterIds: [],
            elementIds: []
        };
        
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return { ...scene, shots: [...scene.shots, newShot] };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveShot = (sceneId, shotId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.filter(s => s.id !== shotId)
                };
            }
            return scene;
        }).filter(scene => scene.shots.length > 0);
        syncToParent(newScenes);
    };

    const handleRemoveScene = (sceneId) => {
        const newScenes = scenes.filter(s => s.id !== sceneId);
        syncToParent(newScenes);
    };

    const handleAddCharacterToShot = (sceneId, shotId, characterId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            const currentIds = shot.characterIds || [];
                            if (currentIds.includes(characterId)) return shot;
                            return { ...shot, characterIds: [...currentIds, characterId] };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveCharacterFromShot = (sceneId, shotId, characterId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            return { ...shot, characterIds: shot.characterIds?.filter(id => id !== characterId) || [] };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleAddElementToShot = (sceneId, shotId, elementId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            const currentIds = shot.elementIds || [];
                            if (currentIds.includes(elementId)) return shot;
                            return { ...shot, elementIds: [...currentIds, elementId] };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleRemoveElementFromShot = (sceneId, shotId, elementId) => {
        const newScenes = scenes.map(scene => {
            if (scene.id === sceneId) {
                return {
                    ...scene,
                    shots: scene.shots.map(shot => {
                        if (shot.id === shotId) {
                            return { ...shot, elementIds: shot.elementIds?.filter(id => id !== elementId) || [] };
                        }
                        return shot;
                    })
                };
            }
            return scene;
        });
        syncToParent(newScenes);
    };

    const handleCreateNewCharacter = (category) => {
        const newChar = {
            id: uuidv4(),
            name: category === 'character' ? 'New Character' : 'New Element',
            description: '',
            category: category,
            referenceImages: [],
            costumeAndAccessoryImages: [],
        };
        
        setEditingCharacter(newChar);
    };

    const handleSaveCharacter = (updatedChar, sceneId, shotId) => {
        console.log('💾 handleSaveCharacter called:', {
            charId: updatedChar.id,
            name: updatedChar.name,
            category: updatedChar.category,
            hasImage: !!updatedChar.image,
            sceneId,
            shotId
        });
        
        // Check if this is a new character (not in project yet)
        const isNew = !projectData.characters.find(c => c.id === updatedChar.id);
        console.log('  Is new character:', isNew);
        
        let updatedChars;
        if (isNew) {
            // Add to project characters
            updatedChars = [...projectData.characters, updatedChar];
            console.log('  Added new character, total characters now:', updatedChars.length);
        } else {
            // Update existing character
            updatedChars = projectData.characters.map(c => c.id === updatedChar.id ? updatedChar : c);
            console.log('  Updated existing character');
        }
        
        // Update project
        console.log('  Calling onProjectUpdate with', updatedChars.length, 'characters');
        onProjectUpdate({ ...projectData, characters: updatedChars });
        
        // If sceneId and shotId provided, add character to that shot
        if (sceneId && shotId && isNew) {
            if (updatedChar.category === 'element') {
                console.log('  Adding element to shot');
                handleAddElementToShot(sceneId, shotId, updatedChar.id);
            } else {
                console.log('  Adding character to shot');
                handleAddCharacterToShot(sceneId, shotId, updatedChar.id);
            }
        }
        
        setEditingCharacter(null);
    };

    const handleGenerateSceneVideos = async (sceneId) => {
        const targetScene = scenes.find(s => s.id === sceneId);
        if (!targetScene) return;

        const shotsNeedingVideos = targetScene.shots.filter(s => {
            if (!s.startFrameImage) return false;
            if (requiresEndFrame && !s.endFrameImage) return false;
            if (s.video) return false;
            return true;
        });

        if (shotsNeedingVideos.length === 0) {
            alert('All shots in this scene already have videos or are missing required frames.');
            return;
        }

        const totalCost = shotsNeedingVideos.length * CREDIT_COSTS.VIDEO_GENERATION;
        if (!confirm(`Generate ${shotsNeedingVideos.length} videos for Scene ${scenes.indexOf(targetScene) + 1} (${totalCost} credits)?`)) return;
        if (onDeductCredits && !onDeductCredits(totalCost)) return;

        setGeneratingVideosForScenes(prev => new Set(prev).add(sceneId));
        setSceneErrors(prev => ({ ...prev, [sceneId]: null }));

        alert(`Scene video generation initiated. For better control and quality, use the "Gen Video" button on individual shots to access advanced settings (dialogue, narration, effects).`);

        setGeneratingVideosForScenes(prev => {
            const newSet = new Set(prev);
            newSet.delete(sceneId);
            return newSet;
        });
    };

    const handleGenerateFrame = async (sceneId, shotId, frameType) => {
        const scene = scenes.find(s => s.id === sceneId);
        const shot = scene?.shots.find(s => s.id === shotId);
        if (!shot) return;

        const prompt = frameType === 'start' ? shot.startFramePrompt : shot.endFramePrompt;
        if (!prompt) {
            alert('Please add a shot description first to generate prompts');
            return;
        }

        // Remove upfront deduction - credits are deducted by backend
        // Just check if user has some credits
        // if (onDeductCredits && !onDeductCredits(CREDIT_COSTS.IMAGE_GENERATION)) return;

        const key = `${shotId}-${frameType}`;
        setGeneratingFrames(prev => ({ ...prev, [key]: true }));

        try {
            // Get characters assigned to this shot
            const chars = projectData.characters.filter(c => shot.characterIds?.includes(c.id));
            
            // Get elements for this shot (via explicit IDs AND smart matching)
            const elements = getElementsForShot(shot);
            
            // Combine characters and elements for entity references
            const allEntities = [...chars, ...elements];
            
            // Build reference images array
            // 1. For end frame: include start frame for continuity
            // 2. Include element images as references
            const refs = [];
            if (frameType === 'end' && shot.startFrameImage) {
                refs.push(shot.startFrameImage);
            }
            
            // Add element reference images
            elements.forEach(elem => {
                if (elem.image) {
                    refs.push(elem.image);
                    console.log(`🎭 Adding element reference: ${elem.name} -> ${elem.image.substring(0, 50)}...`);
                } else {
                    console.log(`⚠️ Element ${elem.name} has no image`);
                }
            });
            
            // Determine if we have reference images to use reference-to-image model
            const hasReferenceImages = refs.length > 0 || allEntities.some(e => e.image);
            const modelId = projectData.modelPreferences?.image?.general?.[hasReferenceImages ? 'referenceToImage' : 'textToImage'];
            
            console.log('🖼️ Frame generation:', {
                frameType,
                shotId,
                characters: chars.map(c => ({ name: c.name, hasImage: !!c.image })),
                elements: elements.map(e => ({ name: e.name, hasImage: !!e.image, id: e.id })),
                shotElementIds: shot.elementIds,
                referenceImages: refs.length,
                hasEntityImages: allEntities.filter(e => e.image).length,
                modelType: hasReferenceImages ? 'referenceToImage' : 'textToImage',
                modelId
            });
            
            const result = await generateSceneImage(prompt, projectData.aspectRatio, allEntities, refs, modelId);
            const image = result.image || result; // Handle both new and old format
            
            console.log('🖼️ generateSceneImage result:', {
                hasImage: !!image,
                creditsUsed: result.creditsUsed,
                creditsRemaining: result.creditsRemaining,
                jobId: result.jobId
            });
            
            const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
            handleShotUpdate(sceneId, shotId, field, image);
            
            // Track credits used from backend response (including remaining credits and job ID)
            if (onTrackCreditsUsed) {
                console.log('💳 Calling onTrackCreditsUsed with:', result.creditsUsed, result.creditsRemaining, result.jobId);
                if (result.creditsUsed > 0) {
                    onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
                }
            } else {
                console.warn('⚠️ onTrackCreditsUsed is not defined');
            }
            
            if (onSaveMedia) onSaveMedia([image]);
        } catch (err) {
            console.error('Frame generation failed:', err);
            alert(`Failed to generate ${frameType} frame: ${err.message}`);
        } finally {
            setGeneratingFrames(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleFrameImageUpload = (e, sceneId, shotId, frameType) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
            handleShotUpdate(sceneId, shotId, field, reader.result);
        };
        reader.readAsDataURL(file);

        if (e.target) e.target.value = '';
    };

    const handleDeleteFrame = (sceneId, shotId, frameType) => {
        const field = frameType === 'start' ? 'startFrameImage' : 'endFrameImage';
        handleShotUpdate(sceneId, shotId, field, null);
    };

    const handleVideoFileChange = (e, shotId) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
            if (sceneId) {
                handleShotUpdate(sceneId, shotId, 'video', reader.result);
            }
        };
        reader.readAsDataURL(file);

        if (e.target) e.target.value = '';
    };

    const handleSaveFromImageEditor = (imageData) => {
        if (editingInfo) {
            const { shot, frame } = editingInfo;
            const sceneId = scenes.find(s => s.shots.some(sh => sh.id === shot.id))?.id;
            if (sceneId) {
                const field = frame === 'start' ? 'startFrameImage' : 'endFrameImage';
                handleShotUpdate(sceneId, shot.id, field, imageData);
            }
            setEditingInfo(null);
        }
    };

    const handleSaveFromPreProduction = (shotId, updates) => {
        const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
        if (sceneId && updates.videoUrl) {
            // Update video and duration atomically to avoid race conditions
            const shotUpdates = { video: updates.videoUrl };
            if (updates.videoDuration) {
                shotUpdates.videoDuration = updates.videoDuration;
            }
            handleShotUpdateMultiple(sceneId, shotId, shotUpdates);
            console.log(`📹 Saved video for shot ${shotId}:`, updates.videoUrl?.substring(0, 60) + '...', 'duration:', updates.videoDuration || 'N/A');
        }
        setPreppingVideoForShot(null);
    };

    const handleSaveFromAnimation = (shotId, videoUrl) => {
        const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
        if (sceneId) {
            handleShotUpdate(sceneId, shotId, 'video', videoUrl);
        }
        setAnimatingShot(null);
    };

    const handleTranscribeAndSaveDescription = async (shotId, audio) => {
        setManagingAudioForShot(null);
        
        try {
            const newDescription = await transcribeShotNarration(audio.data);
            const sceneId = scenes.find(s => s.shots.some(shot => shot.id === shotId))?.id;
            if (sceneId) {
                handleShotUpdate(sceneId, shotId, 'description', newDescription);
            }
        } catch (err) {
            console.error("Transcription failed:", err);
            alert(`Transcription failed: ${err.message}`);
        }
    };

    const handleSubmit = () => {
        onSubmit(scenes);
    };

    /**
     * Smart character matching for a shot
     * Looks at shot description, start frame prompt, and end frame prompt to find matching characters
     */
    const getCharactersForShot = (shot) => {
        const allCharacters = projectData.characters?.filter(c => 
            (c.category ?? 'character') === 'character' || c.category === 'narrator'
        ) || [];
        
        if (allCharacters.length === 0) return [];
        
        // Combine all text to search for character mentions
        const searchText = [
            shot.description || '',
            shot.startFramePrompt || '',
            shot.endFramePrompt || '',
            shot.title || ''
        ].join(' ').toLowerCase();
        
        // First try explicit characterIds if available
        const explicitCharacters = allCharacters.filter(char => shot.characterIds?.includes(char.id));
        
        // Then do smart matching based on text content
        const matchedCharacters = allCharacters.filter(character => {
            const charName = (character.name || '').toLowerCase();
            
            // Skip if already matched via characterIds
            if (shot.characterIds?.includes(character.id)) return false;
            
            // Check for name match (case-insensitive)
            if (searchText.includes(charName)) {
                console.log(`👤 Character match: "${character.name}" found in shot text`);
                return true;
            }
            
            // Check for first name match (for names like "Viriya Kumar" matching "Viriya")
            const firstName = charName.split(/\s+/)[0];
            if (firstName && firstName.length > 2 && searchText.includes(firstName)) {
                console.log(`👤 Character first name match: "${character.name}" matched via "${firstName}"`);
                return true;
            }
            
            return false;
        });
        
        const allMatchedCharacters = [...explicitCharacters, ...matchedCharacters];
        
        if (allMatchedCharacters.length > 0) {
            console.log(`👤 Shot "${shot.title || shot.id}": Matched ${allMatchedCharacters.length} characters:`, allMatchedCharacters.map(c => c.name));
        }
        
        return allMatchedCharacters;
    };

    /**
     * Smart element matching for a shot
     * Looks at shot description, start frame prompt, and end frame prompt to find matching elements
     * Uses fuzzy matching to handle variations like "cream" vs "cream jar", case differences, etc.
     */
    const getElementsForShot = (shot) => {
        const allElements = projectData.characters?.filter(c => c.category === 'element') || [];
        
        if (allElements.length === 0) return [];
        
        // Combine all text to search for element mentions (including frame prompts)
        const searchText = [
            shot.description || '',
            shot.startFramePrompt || '',
            shot.endFramePrompt || '',
            shot.title || '',
            shot.narration || ''
        ].join(' ').toLowerCase();
        
        // Also get words from the project script/story for context matching
        const storyContext = (projectData.script || '').toLowerCase();
        
        // First try explicit elementIds if available
        const explicitElements = allElements.filter(el => shot.elementIds?.includes(el.id));
        
        // Then do smart matching based on text content
        const matchedElements = allElements.filter(element => {
            const elementName = (element.name || '').toLowerCase();
            const elementDesc = (element.description || '').toLowerCase();
            
            // Skip if already matched via elementIds
            if (shot.elementIds?.includes(element.id)) return false;
            
            // Generate variations for matching
            const nameWords = elementName.split(/\s+/).filter(w => w.length > 2);
            const significantWords = nameWords.filter(w => 
                !['the', 'a', 'an', 'of', 'in', 'on', 'at', 'to', 'for', 'with', 'jar', 'bottle', 'box', 'container', 'pack', 'tube'].includes(w)
            );
            
            // Also get product-type words separately (these help identify if we're talking about the same category)
            const productTypeWords = nameWords.filter(w => 
                ['jar', 'bottle', 'box', 'container', 'pack', 'tube', 'cream', 'lotion', 'serum', 'oil', 'product'].includes(w)
            );
            
            // Check for exact name match (case-insensitive)
            if (searchText.includes(elementName)) {
                console.log(`🎭 Exact match: "${element.name}" found in shot text`);
                return true;
            }
            
            // Check for partial match - element name contains search keywords
            // e.g., "Viriya cream" should match "Viriya Cream Jar"
            const searchWords = searchText.split(/\s+/).filter(w => w.length > 2);
            
            // Check if significant words from element name appear in search text
            // For product names like "Viriya Cream Jar", we want to match on brand ("Viriya") + category ("cream")
            const matchingSignificantWords = significantWords.filter(word => searchText.includes(word));
            const matchingProductWords = productTypeWords.filter(word => searchText.includes(word));
            
            // Match if:
            // 1. At least 2 significant words match, OR
            // 2. 1 significant word matches AND at least 1 product type word matches, OR
            // 3. Only 1 significant word exists and it matches (single-word element names)
            if (matchingSignificantWords.length >= 2) {
                console.log(`🎭 Multi-word match: "${element.name}" matched via [${matchingSignificantWords.join(', ')}]`);
                return true;
            }
            
            if (matchingSignificantWords.length >= 1 && matchingProductWords.length >= 1) {
                console.log(`🎭 Brand+Product match: "${element.name}" matched via brand [${matchingSignificantWords.join(', ')}] + product [${matchingProductWords.join(', ')}]`);
                return true;
            }
            
            if (matchingSignificantWords.length === 1 && significantWords.length === 1) {
                console.log(`🎭 Single-word match: "${element.name}" matched via "${matchingSignificantWords[0]}"`);
                return true;
            }
            
            // Check if element name appears in search text with minor variations
            // e.g., "cream jar" matches "cream" 
            for (const searchWord of searchWords) {
                // Skip common words
                if (['the', 'and', 'she', 'her', 'his', 'with', 'from', 'into', 'that', 'this'].includes(searchWord)) continue;
                
                if (searchWord.length > 4 && elementName.includes(searchWord)) {
                    console.log(`🎭 Partial match: "${element.name}" matched via "${searchWord}"`);
                    return true;
                }
            }
            
            // Check description-based matching
            if (elementDesc) {
                const descWords = elementDesc.split(/\s+/).filter(w => w.length > 3);
                const descMatches = descWords.filter(word => searchText.includes(word.toLowerCase()));
                if (descMatches.length >= 2) {
                    console.log(`🎭 Description match: "${element.name}" matched via description words [${descMatches.join(', ')}]`);
                    return true;
                }
            }
            
            // Check if element name words appear consecutively in search text (brand name check)
            // e.g., "Viriya cream" in text should match element "Viriya Cream Jar"
            if (significantWords.length >= 1) {
                const brandWord = significantWords[0]; // Assume first word is brand
                if (brandWord.length >= 4 && searchText.includes(brandWord)) {
                    // Check if any product word also appears nearby
                    const brandIndex = searchText.indexOf(brandWord);
                    const nearbyText = searchText.substring(Math.max(0, brandIndex - 20), brandIndex + brandWord.length + 20);
                    const hasProductContext = productTypeWords.some(pw => nearbyText.includes(pw)) ||
                                              ['cream', 'lotion', 'serum', 'product', 'jar', 'bottle'].some(pw => nearbyText.includes(pw));
                    if (hasProductContext) {
                        console.log(`🎭 Brand-context match: "${element.name}" matched via brand "${brandWord}" with product context`);
                        return true;
                    }
                }
            }
            
            return false;
        });
        
        const allMatchedElements = [...explicitElements, ...matchedElements];
        
        if (allMatchedElements.length > 0) {
            console.log(`🎭 Shot "${shot.title || shot.id}": Matched ${allMatchedElements.length} elements:`, allMatchedElements.map(e => e.name));
        }
        
        return allMatchedElements;
    };

    const getSceneImageModelId = (hasReferences) => {
        const generalPrefs = projectData.modelPreferences?.image?.general;
        const pref = hasReferences ? generalPrefs?.referenceToImage : generalPrefs?.textToImage;
        return pref;
    };

    const handleGenerateSceneImages = async (sceneId) => {
        const targetScene = scenes.find(s => s.id === sceneId);
        if (!targetScene) return;

        // Calculate frames to generate with shot continuity in mind:
        // - Shot 1: May need both start and end frames
        // - Shot 2+: Start frame is REUSED from previous shot's end frame, only end frame needs generation
        let framesToGenerate = 0;
        for (let i = 0; i < targetScene.shots.length; i++) {
            const shot = targetScene.shots[i];
            
            if (i === 0) {
                // First shot: may need start frame
                if (!shot.startFrameImage) framesToGenerate++;
            }
            // For shots 2+: start frame will be reused from previous end frame (no generation needed)
            
            // All shots: may need end frame
            if (!shot.endFrameImage) framesToGenerate++;
        }
        
        if (framesToGenerate === 0) {
            alert('All frames in this scene already have images.');
            return;
        }
        
        const totalCost = framesToGenerate * CREDIT_COSTS.IMAGE_GENERATION;
        if (!confirm(`Generate ${framesToGenerate} frame${framesToGenerate > 1 ? 's' : ''} for Scene ${scenes.indexOf(targetScene) + 1}? (Est. ${totalCost} credits)\n\nNote: Shot continuity is enabled - end frames will be reused as start frames for subsequent shots.`)) return;
        // Credits are deducted by backend - just proceed with generation

        setGeneratingScenes(prev => new Set(prev).add(sceneId));
        setSceneErrors(prev => ({ ...prev, [sceneId]: null }));

        const generatedImages = [];
        let totalCreditsUsed = 0;
        let lastCreditsRemaining = null;
        const generatedJobIds = [];

        try {
            await preloadProjectAssets(projectData);
            let previousEndFrame = undefined;
            let currentScenesState = [...scenes];

            for (let i = 0; i < targetScene.shots.length; i++) {
                const shot = targetScene.shots[i];
                const shotIndex = i; // 0-based index
                const charactersInShot = getCharactersForShot(shot);
                const elementsInShot = getElementsForShot(shot);
                const allEntities = [...charactersInShot, ...elementsInShot];
                
                let startFrameImage;
                
                // SHOT CONTINUITY LOGIC:
                // For shot 1: Generate fresh start frame
                // For shot 2+: ALWAYS use previous shot's end frame as start frame
                if (shotIndex === 0) {
                    // First shot: Use existing start frame or generate new one
                    startFrameImage = shot.startFrameImage;
                    
                    if (!startFrameImage && shot.startFramePrompt) {
                        const hasInputImages = allEntities.some(c => c.image);
                        const modelId = getSceneImageModelId(hasInputImages);
                        
                        console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot 1: Generating FRESH start frame`);
                        const result = await generateSceneImage(shot.startFramePrompt, projectData.aspectRatio, allEntities, [], modelId);
                        startFrameImage = result.image || result;
                        if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                        if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                        if (result.jobId) generatedJobIds.push(result.jobId);
                        if (startFrameImage) generatedImages.push(startFrameImage);
                    }
                } else {
                    // Subsequent shots: ALWAYS use previous end frame for continuity
                    if (previousEndFrame) {
                        console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: REUSING previous end frame as start frame (continuity)`);
                        startFrameImage = previousEndFrame;
                    } else {
                        // Fallback: If no previous end frame (shouldn't happen normally), use existing or generate
                        console.warn(`⚠️ Shot ${shotIndex + 1}: No previous end frame available for continuity, generating fresh`);
                        startFrameImage = shot.startFrameImage;
                        
                        if (!startFrameImage && shot.startFramePrompt) {
                            const hasInputImages = allEntities.some(c => c.image);
                            const modelId = getSceneImageModelId(hasInputImages);
                            const result = await generateSceneImage(shot.startFramePrompt, projectData.aspectRatio, allEntities, [], modelId);
                            startFrameImage = result.image || result;
                            if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                            if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                            if (result.jobId) generatedJobIds.push(result.jobId);
                            if (startFrameImage) generatedImages.push(startFrameImage);
                        }
                    }
                }
                
                // Always generate end frame (fresh for each shot)
                const referencesForEndFrame = startFrameImage ? [startFrameImage] : [];
                let endFrameImage = shot.endFrameImage;
                
                if (shot.endFramePrompt && !shot.endFrameImage) {
                    console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: Generating end frame`);
                    console.log(`  🔍 endFramePrompt: ${shot.endFramePrompt?.substring(0, 50)}...`);
                    try {
                        const result = await generateSceneImage(shot.endFramePrompt, projectData.aspectRatio, allEntities, referencesForEndFrame, getSceneImageModelId(true));
                        endFrameImage = result.image || result;
                        if (result.creditsUsed) totalCreditsUsed += result.creditsUsed;
                        if (result.creditsRemaining !== undefined) lastCreditsRemaining = result.creditsRemaining;
                        if (result.jobId) generatedJobIds.push(result.jobId);
                        console.log(`  ✅ End frame generated: ${endFrameImage ? endFrameImage.substring(0, 50) + '...' : 'NULL'}`);
                        if (endFrameImage) generatedImages.push(endFrameImage);
                    } catch (genErr) {
                        console.error(`  ❌ End frame generation error:`, genErr);
                    }
                } else {
                    console.log(`🎬 Scene ${scenes.findIndex(s => s.id === sceneId) + 1}, Shot ${shotIndex + 1}: Skipping end frame (prompt=${!!shot.endFramePrompt}, existing=${!!shot.endFrameImage})`);
                }

                currentScenesState = currentScenesState.map(s => {
                    if (s.id === sceneId) {
                        return { ...s, shots: s.shots.map(os => os.id === shot.id ? { ...os, startFrameImage: startFrameImage, endFrameImage: endFrameImage } : os) };
                    }
                    return s;
                });
                
                // Debug: Log what we're saving
                const updatedScene = currentScenesState.find(s => s.id === sceneId);
                const updatedShot = updatedScene?.shots.find(s => s.id === shot.id);
                console.log(`💾 Saving images for Shot ${shotIndex + 1}:`, {
                    startFrameImage: startFrameImage ? startFrameImage.substring(0, 50) + '...' : 'null',
                    endFrameImage: endFrameImage ? endFrameImage.substring(0, 50) + '...' : 'null',
                    shotId: shot.id,
                    updatedInState: !!updatedShot?.startFrameImage || !!updatedShot?.endFrameImage
                });
                
                syncToParent(currentScenesState);
                
                // Store end frame for next shot's continuity
                previousEndFrame = endFrameImage;
            }

            if (onSaveMedia && generatedImages.length > 0) {
                onSaveMedia(generatedImages);
            }
            
            // Track total credits used from all image generations
            if (onTrackCreditsUsed && totalCreditsUsed > 0) {
                // Pass last remaining credits and all job IDs
                onTrackCreditsUsed(totalCreditsUsed, lastCreditsRemaining, generatedJobIds.length > 0 ? generatedJobIds[generatedJobIds.length - 1] : null);
            }

        } catch (err) {
            console.error("Scene image generation failed:", err);
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during image generation.";
            setSceneErrors(prev => ({ ...prev, [sceneId]: errorMessage }));
        } finally {
            setGeneratingScenes(prev => {
                const newSet = new Set(prev);
                newSet.delete(sceneId);
                return newSet;
            });
        }
    };

    const handleGenerateAllImages = async () => {
        // Calculate frames with continuity in mind
        // Each scene: Shot 1 needs start+end, Shots 2+ only need end (start reused from previous)
        let totalFrames = 0;
        for (const scene of scenes) {
            for (let i = 0; i < scene.shots.length; i++) {
                const shot = scene.shots[i];
                if (i === 0) {
                    // First shot: may need start frame
                    if (!shot.startFrameImage) totalFrames++;
                }
                // All shots: may need end frame
                if (!shot.endFrameImage) totalFrames++;
            }
        }

        if (totalFrames === 0) {
            alert('All frames already have images.');
            return;
        }

        const totalCost = totalFrames * CREDIT_COSTS.IMAGE_GENERATION;
        if (!confirm(`Generate ${totalFrames} frame${totalFrames > 1 ? 's' : ''} across all scenes (Est. ${totalCost} credits)?\n\nNote: Shot continuity is enabled - end frames will be reused as start frames for subsequent shots within each scene.`)) return;
        // Credits are deducted by backend - just proceed with generation

        setIsGeneratingAll(true);
        setGenerationProgress({ current: 0, total: scenes.length, message: 'Starting batch generation...' });

        try {
            await preloadProjectAssets(projectData, (msg) => setGenerationProgress(prev => ({ ...prev, message: msg })));

            for (let i = 0; i < scenes.length; i++) {
                setGenerationProgress({ current: i, total: scenes.length, message: `Processing Scene ${i + 1}...` });
                await handleGenerateSceneImages(scenes[i].id);
            }
            
            setGenerationProgress({ current: scenes.length, total: scenes.length, message: 'All images generated!' });
        } catch (err) {
            console.error('Batch generation failed:', err);
        } finally {
            setTimeout(() => setIsGeneratingAll(false), 2000);
        }
    };

    const handleGenerateAllVideos = async () => {
        const allShots = scenes.flatMap(s => s.shots).filter(s => s.startFrameImage && !s.video);
        const totalCost = allShots.length * CREDIT_COSTS.VIDEO_GENERATION;
        
        if (!confirm(`Generate videos for ${allShots.length} shots (Est. ${totalCost} credits)?`)) return;
        // Credits are deducted by backend - just proceed with generation

        setIsGeneratingAllVideos(true);
        setGenerationProgress({ current: 0, total: allShots.length, message: 'Starting video generation...' });

        try {
            for (let i = 0; i < allShots.length; i++) {
                const shot = allShots[i];
                setGenerationProgress({ current: i, total: allShots.length, message: `Generating video ${i + 1}/${allShots.length}...` });
                
                // This is placeholder - actual implementation would call video generation
                // For now, we'll show the modal for each shot
                console.log(`Would generate video for shot ${shot.id}`);
            }
            
            setGenerationProgress({ current: allShots.length, total: allShots.length, message: 'All videos queued!' });
            alert('Batch video generation completed. Generate videos individually using the Gen Video button for better control.');
        } catch (err) {
            console.error('Batch video generation failed:', err);
        } finally {
            setTimeout(() => setIsGeneratingAllVideos(false), 2000);
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-[#0a0a0f]">
            {/* Global Progress Overlay */}
            {(isGeneratingAll || isGeneratingAllVideos) && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-gray-900 p-8 rounded-lg border border-gray-700 max-w-md w-full text-center">
                        <LoadingSpinner className="w-12 h-12 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">{isGeneratingAll ? 'Generating Images' : 'Generating Videos'}</h3>
                        <p className="text-gray-400 mb-4">{generationProgress.message}</p>
                        <div className="w-full bg-gray-700 rounded-full h-2.5">
                            <div 
                                className="bg-purple-600 h-2.5 rounded-full transition-all duration-500" 
                                style={{ width: generationProgress.total > 0 ? `${(generationProgress.current / generationProgress.total) * 100}%` : '0%' }}
                            ></div>
                        </div>
                        <p className="mt-2 text-sm text-gray-400">
                            {generationProgress.current} / {generationProgress.total} completed
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="p-4 border-b border-gray-800 flex justify-between items-center shrink-0">
                <div>
                    <h2 className="text-xl font-bold">Storyboard Editor</h2>
                    <p className="text-sm text-gray-400">Refine the automatically generated shots or add your own.</p>
                </div>
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => setShowAIToolkitModal(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                        title="Change AI model preferences"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        AI Settings
                    </button>
                    <button
                        onClick={handleGenerateAllImages}
                        disabled={isGeneratingAll || isGeneratingAllVideos}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                    >
                        {isGeneratingAll ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-2" />}
                        {isGeneratingAll ? 'Generating...' : 'Generate All Images'}
                    </button>
                    <button
                        onClick={handleGenerateAllVideos}
                        disabled={isGeneratingAll || isGeneratingAllVideos}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                    >
                        {isGeneratingAllVideos ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <FilmIcon className="w-4 h-4 mr-2" />}
                        {isGeneratingAllVideos ? 'Generating...' : 'Generate All Videos'}
                    </button>
                    <button
                        onClick={onBack}
                        className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        Back
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-sm"
                    >
                        Continue to Studio
                        <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </div>

            {/* Main Content - Horizontal Scenes with Vertical Shots */}
            <div className="flex-grow flex p-8 space-x-8 overflow-x-auto">
                {scenes.map((scene, sceneIndex) => (
                    <div key={scene.id} className="w-[450px] flex-shrink-0 bg-gray-900/50 rounded-lg p-4 flex flex-col space-y-4">
                        {/* Scene Header */}
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-bold text-purple-400">Scene {sceneIndex + 1}</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleGenerateSceneImages(scene.id)}
                                    disabled={generatingScenes.has(scene.id) || isGeneratingAll}
                                    title="Generate all images for this scene"
                                    className="bg-green-600/50 hover:bg-green-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded-md transition-colors text-xs flex items-center"
                                >
                                    {generatingScenes.has(scene.id) ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-1" />}
                                    {generatingScenes.has(scene.id) ? 'Generating...' : 'Gen Images'}
                                </button>
                                <button
                                    onClick={() => handleGenerateSceneVideos(scene.id)}
                                    disabled={generatingVideosForScenes.has(scene.id) || isGeneratingAllVideos}
                                    title="Generate all videos for this scene"
                                    className="bg-blue-600/50 hover:bg-blue-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded-md transition-colors text-xs flex items-center"
                                >
                                    {generatingVideosForScenes.has(scene.id) ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <FilmIcon className="w-4 h-4 mr-1" />}
                                    {generatingVideosForScenes.has(scene.id) ? 'Generating...' : 'Gen Videos'}
                                </button>
                                <button
                                    onClick={() => handleAddShot(scene.id)}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-1 px-3 rounded-md transition-colors text-xs flex items-center"
                                    title="Add shot to this scene"
                                >
                                    <PlusIcon className="w-4 h-4 mr-1" />
                                    Shot
                                </button>
                                <button
                                    onClick={() => handleRemoveScene(scene.id)}
                                    className="text-red-500 hover:text-red-400 text-xs font-bold p-1"
                                    title="Remove this scene"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {sceneErrors[scene.id] && (
                            <div className="text-xs text-red-400 bg-red-500/10 p-2 rounded">
                                {sceneErrors[scene.id]}
                            </div>
                        )}

                        {/* Shots - Vertical Layout */}
                        <div className="relative space-y-3 overflow-y-auto pr-2 max-h-[calc(100vh-250px)]">
                            {(generatingScenes.has(scene.id) || generatingVideosForScenes.has(scene.id)) && (
                                <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
                                    <div className="text-center">
                                        <LoadingSpinner className="w-8 h-8 mx-auto mb-2" />
                                        <p className="text-gray-300 font-semibold">
                                            {generatingVideosForScenes.has(scene.id) ? 'Generating scene videos...' : 'Generating scene images...'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {scene.shots.map((shot, shotIndex) => {
                                if (!videoInputRefs.current[shot.id]) {
                                    videoInputRefs.current[shot.id] = null;
                                }
                                const isUpdatingPrompts = updatingPromptsForShotId === shot.id;
                                const isGeneratingStart = generatingFrames[`${shot.id}-start`];
                                const isGeneratingEnd = generatingFrames[`${shot.id}-end`];

                                return (
                                    <div key={shot.id} className="bg-gray-800 p-3 rounded-md space-y-3">
                                        {/* Hidden file inputs */}
                                        <input
                                            type="file"
                                            accept="video/*"
                                            className="hidden"
                                            ref={el => { videoInputRefs.current[shot.id] = el; }}
                                            onChange={(e) => handleVideoFileChange(e, shot.id)}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => { frameInputRefs.current[`${shot.id}-start`] = el; }}
                                            onChange={(e) => handleFrameImageUpload(e, scene.id, shot.id, 'start')}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => { frameInputRefs.current[`${shot.id}-end`] = el; }}
                                            onChange={(e) => handleFrameImageUpload(e, scene.id, shot.id, 'end')}
                                        />

                                        {/* Shot Header */}
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-semibold text-gray-300">Shot {shotIndex + 1}</h4>
                                            <button 
                                                onClick={() => handleRemoveShot(scene.id, shot.id)} 
                                                className="text-red-500 hover:text-red-400 text-xs font-bold"
                                            >
                                                REMOVE
                                            </button>
                                        </div>

                                        {/* Metadata Display */}
                                        {(shot.cinematography || shot.sound || shot.vfx) && (
                                            <div className="text-xs bg-gray-900/50 p-2 rounded-md space-y-1">
                                                {shot.cinematography && (
                                                    <div className="flex gap-2 text-gray-400">
                                                        <span className="text-purple-300 font-semibold">🎥 Cam:</span> 
                                                        {shot.cinematography.shotSize}, {shot.cinematography.cameraAngle}, {shot.cinematography.movement}
                                                    </div>
                                                )}
                                                {shot.sound && (
                                                    <div className="flex gap-2 text-gray-400">
                                                        <span className="text-green-300 font-semibold">🎵 Sound:</span> 
                                                        <span className="truncate">{shot.sound.music} | {shot.sound.sfx}</span>
                                                    </div>
                                                )}
                                                {shot.vfx && shot.vfx !== "None" && (
                                                    <div className="flex gap-2 text-gray-400">
                                                        <span className="text-blue-300 font-semibold">✨ VFX:</span> 
                                                        {shot.vfx}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Shot Description */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Description</label>
                                            <textarea
                                                value={shot.description || ''}
                                                onChange={(e) => handleDescriptionChange(scene.id, shot.id, e.target.value)}
                                                rows={3}
                                                className="w-full p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                placeholder="Describe what happens in this shot..."
                                            />
                                            {isUpdatingPrompts && (
                                                <p className="text-xs text-purple-400 mt-1 flex items-center">
                                                    <LoadingSpinner className="w-3 h-3 mr-1" />
                                                    Generating prompts...
                                                </p>
                                            )}
                                        </div>

                                        {/* Frame Images - Side by Side */}
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* Start Frame */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-400 uppercase flex items-center gap-2">
                                                    Start Frame
                                                    {shotIndex > 0 && (
                                                        <span 
                                                            className={`text-[10px] font-normal px-1.5 py-0.5 rounded ${
                                                                scene.shots[shotIndex - 1]?.endFrameImage 
                                                                    ? 'text-green-400 bg-green-500/20' 
                                                                    : 'text-cyan-400 bg-cyan-500/20'
                                                            }`} 
                                                            title={scene.shots[shotIndex - 1]?.endFrameImage 
                                                                ? "Linked from previous shot's end frame" 
                                                                : "Will be linked from previous shot's end frame when generated"
                                                            }
                                                        >
                                                            🔗 {scene.shots[shotIndex - 1]?.endFrameImage ? 'Linked' : 'Continuity'}
                                                        </span>
                                                    )}
                                                    {shot.video && (
                                                        <span 
                                                            className="text-[10px] font-normal px-1.5 py-0.5 rounded text-purple-400 bg-purple-500/20"
                                                            title={`Video saved (${shot.videoDuration ? shot.videoDuration.toFixed(1) + 's' : 'duration unknown'})`}
                                                        >
                                                            🎬 Video Ready
                                                        </span>
                                                    )}
                                                </label>
                                                <div className="relative group aspect-video bg-gray-900 rounded-md overflow-hidden">
                                                    {shot.startFrameImage ? (
                                                        <>
                                                            <img 
                                                                src={shot.startFrameImage} 
                                                                alt="Start" 
                                                                className="w-full h-full object-cover"
                                                                crossOrigin="anonymous"
                                                                referrerPolicy="no-referrer"
                                                                onError={(e) => {
                                                                    console.warn('Start frame failed to load:', shot.startFrameImage?.substring(0, 60));
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                            {/* Video indicator overlay */}
                                                            {shot.video && (
                                                                <div className="absolute top-1 right-1 bg-purple-600/90 px-1.5 py-0.5 rounded text-[10px] text-white font-medium flex items-center gap-1">
                                                                    🎬 {shot.videoDuration ? `${shot.videoDuration.toFixed(1)}s` : 'Video'}
                                                                </div>
                                                            )}
                                                            {/* View button overlay on hover */}
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button
                                                                    onClick={() => setViewingFrameImage({ url: shot.startFrameImage, title: `Start Frame - Shot ${shot.id}` })}
                                                                    className="p-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white"
                                                                    title="View full image"
                                                                >
                                                                    <EyeIcon className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <PhotoIcon className="w-8 h-8 text-gray-600" />
                                                        </div>
                                                    )}
                                                    {isGeneratingStart && (
                                                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                                                            <LoadingSpinner className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 line-clamp-2 min-h-[2rem]">
                                                    {shot.startFramePrompt || 'No prompt yet'}
                                                </p>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleGenerateFrame(scene.id, shot.id, 'start')}
                                                        disabled={isGeneratingStart || !shot.startFramePrompt}
                                                        className="flex-1 bg-green-600/50 hover:bg-green-600 disabled:bg-gray-700 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Generate start frame"
                                                    >
                                                        <MagicWandIcon className="w-3 h-3 mr-1" />
                                                        Gen
                                                    </button>
                                                    <button
                                                        onClick={() => frameInputRefs.current[`${shot.id}-start`]?.click()}
                                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Upload start frame"
                                                    >
                                                        <UploadIcon className="w-3 h-3 mr-1" />
                                                        Up
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingInfo({ shot, frame: 'start' })}
                                                        className="flex-1 bg-purple-600/50 hover:bg-purple-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Edit start frame"
                                                    >
                                                        <PencilIcon className="w-3 h-3 mr-1" />
                                                        Ed
                                                    </button>
                                                    {shot.startFrameImage && (
                                                        <button
                                                            onClick={() => handleDeleteFrame(scene.id, shot.id, 'start')}
                                                            className="bg-red-600/50 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                                            title="Delete start frame"
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* End Frame */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-400 uppercase">End Frame</label>
                                                <div className="relative group aspect-video bg-gray-900 rounded-md overflow-hidden">
                                                    {shot.endFrameImage ? (
                                                        <>
                                                            <img 
                                                                src={shot.endFrameImage} 
                                                                alt="End" 
                                                                className="w-full h-full object-cover"
                                                                crossOrigin="anonymous"
                                                                referrerPolicy="no-referrer"
                                                                onError={(e) => {
                                                                    console.warn('End frame failed to load:', shot.endFrameImage?.substring(0, 60));
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                            {/* View button overlay on hover */}
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button
                                                                    onClick={() => setViewingFrameImage({ url: shot.endFrameImage, title: `End Frame - Shot ${shot.id}` })}
                                                                    className="p-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white"
                                                                    title="View full image"
                                                                >
                                                                    <EyeIcon className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <PhotoIcon className="w-8 h-8 text-gray-600" />
                                                        </div>
                                                    )}
                                                    {isGeneratingEnd && (
                                                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                                                            <LoadingSpinner className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 line-clamp-2 min-h-[2rem]">
                                                    {shot.endFramePrompt || 'No prompt yet'}
                                                </p>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleGenerateFrame(scene.id, shot.id, 'end')}
                                                        disabled={isGeneratingEnd || !shot.endFramePrompt}
                                                        className="flex-1 bg-green-600/50 hover:bg-green-600 disabled:bg-gray-700 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Generate end frame"
                                                    >
                                                        <MagicWandIcon className="w-3 h-3 mr-1" />
                                                        Gen
                                                    </button>
                                                    <button
                                                        onClick={() => frameInputRefs.current[`${shot.id}-end`]?.click()}
                                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Upload end frame"
                                                    >
                                                        <UploadIcon className="w-3 h-3 mr-1" />
                                                        Up
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingInfo({ shot, frame: 'end' })}
                                                        className="flex-1 bg-purple-600/50 hover:bg-purple-600 text-white text-xs py-1 rounded flex items-center justify-center"
                                                        title="Edit end frame"
                                                    >
                                                        <PencilIcon className="w-3 h-3 mr-1" />
                                                        Ed
                                                    </button>
                                                    {shot.endFrameImage && (
                                                        <button
                                                            onClick={() => handleDeleteFrame(scene.id, shot.id, 'end')}
                                                            className="bg-red-600/50 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                                            title="Delete end frame"
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Characters */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Characters</label>
                                            <div className="flex flex-wrap gap-1">
                                                {shot.characterIds?.map(charId => {
                                                    const char = projectData.characters.find(c => c.id === charId);
                                                    return char ? (
                                                        <span 
                                                            key={charId} 
                                                            className="group px-2 py-0.5 bg-purple-600/30 text-purple-300 rounded text-xs border border-purple-600/50 flex items-center gap-1 cursor-pointer hover:bg-purple-600/50"
                                                            onClick={() => setEditingCharacter(char)}
                                                            title="Click to edit character"
                                                        >
                                                            {char.name}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveCharacterFromShot(scene.id, shot.id, charId);
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ) : null;
                                                })}
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value === '__new__') {
                                                            setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'character' });
                                                            handleCreateNewCharacter('character');
                                                        } else if (e.target.value) {
                                                            handleAddCharacterToShot(scene.id, shot.id, e.target.value);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                    className="px-2 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-xs border-0 cursor-pointer"
                                                    value=""
                                                >
                                                    <option value="">+ Add</option>
                                                    <option value="__new__" className="font-bold text-green-400">✨ Create New Character</option>
                                                    <option disabled>──────────</option>
                                                    {projectData.characters
                                                        .filter(c => (c.category ?? 'character') === 'character' && !shot.characterIds?.includes(c.id))
                                                        .map(char => (
                                                            <option key={char.id} value={char.id}>{char.name}</option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>

                                        {/* Other Elements */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Other Elements</label>
                                            <div className="flex flex-wrap gap-1">
                                                {/* Explicitly selected elements */}
                                                {shot.elementIds?.map(elemId => {
                                                    const elem = projectData.characters.find(c => c.id === elemId);
                                                    return elem ? (
                                                        <span 
                                                            key={elemId} 
                                                            className="group px-2 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-xs border border-cyan-600/50 flex items-center gap-1"
                                                        >
                                                            {elem.name}
                                                            <button
                                                                onClick={() => {
                                                                    setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'element' });
                                                                    setEditingCharacter(elem);
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 hover:text-blue-400"
                                                                title="Edit element"
                                                            >
                                                                ✎
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveElementFromShot(scene.id, shot.id, elemId)}
                                                                className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                                                                title="Remove from shot"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ) : null;
                                                })}
                                                {/* Smart-matched elements (auto-detected from shot text) */}
                                                {(() => {
                                                    const smartMatchedElements = getElementsForShot(shot).filter(
                                                        elem => !shot.elementIds?.includes(elem.id)
                                                    );
                                                    return smartMatchedElements.map(elem => (
                                                        <span 
                                                            key={`smart-${elem.id}`}
                                                            className="group px-2 py-0.5 bg-yellow-600/20 text-yellow-300 rounded text-xs border border-yellow-600/40 border-dashed flex items-center gap-1"
                                                            title="Auto-detected from shot description. Click ✓ to confirm."
                                                        >
                                                            <span className="text-yellow-500">⚡</span>
                                                            {elem.name}
                                                            <button
                                                                onClick={() => {
                                                                    setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'element' });
                                                                    setEditingCharacter(elem);
                                                                }}
                                                                className="hover:text-blue-400 text-yellow-400"
                                                                title="Edit element"
                                                            >
                                                                ✎
                                                            </button>
                                                            <button
                                                                onClick={() => handleAddElementToShot(scene.id, shot.id, elem.id)}
                                                                className="hover:text-green-400 text-yellow-400"
                                                                title="Confirm this element"
                                                            >
                                                                ✓
                                                            </button>
                                                        </span>
                                                    ));
                                                })()}
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value === '__new__') {
                                                            setEditingCharacterForShot({ sceneId: scene.id, shotId: shot.id, category: 'element' });
                                                            handleCreateNewCharacter('element');
                                                        } else if (e.target.value) {
                                                            handleAddElementToShot(scene.id, shot.id, e.target.value);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                    className="px-2 py-0.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-xs border-0 cursor-pointer"
                                                    value=""
                                                >
                                                    <option value="">+ Add</option>
                                                    <option value="__new__" className="font-bold text-green-400">✨ Create New Element</option>
                                                    <option disabled>──────────</option>
                                                    {projectData.characters
                                                        .filter(c => c.category === 'element' && !shot.elementIds?.includes(c.id))
                                                        .map(elem => (
                                                            <option key={elem.id} value={elem.id}>{elem.name}</option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>

                                        {/* Narration */}
                                        <div>
                                            <label className="text-xs font-semibold text-gray-400 mb-1 block uppercase">Description from Narration</label>
                                            <button
                                                onClick={() => setManagingAudioForShot(shot)}
                                                className="w-full py-2 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-md flex items-center justify-center gap-2 text-sm text-gray-300 transition-colors"
                                            >
                                                <MicrophoneIcon className="w-4 h-4" />
                                                Record / Upload to Transcribe
                                            </button>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-3 gap-2 pt-2">
                                            <button
                                                onClick={() => shot.video && setPreviewingVideoUrl(shot.video)}
                                                disabled={!shot.video}
                                                className="bg-blue-600/50 hover:bg-blue-600 disabled:bg-gray-700 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                            >
                                                <PlayIcon className="w-3 h-3" />
                                                Preview
                                            </button>
                                            <button
                                                onClick={() => setPreppingVideoForShot(shot)}
                                                disabled={!shot.startFrameImage}
                                                className="bg-indigo-600/50 hover:bg-indigo-600 disabled:bg-gray-700 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                title={!shot.startFrameImage ? 'Generate start frame first' : 'Generate video'}
                                            >
                                                <FilmIcon className="w-3 h-3" />
                                                Gen Video
                                            </button>
                                            <button
                                                onClick={() => setAnimatingShot(shot)}
                                                disabled={!shot.startFrameImage || shot.video}
                                                className="bg-purple-600/50 hover:bg-purple-600 disabled:bg-gray-700 disabled:opacity-50 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                title={!shot.startFrameImage ? 'Generate start frame first' : shot.video ? 'Video already exists - use Edit' : 'Animate shot'}
                                            >
                                                🎬 Animate
                                            </button>
                                            {shot.video && (
                                                <button
                                                    onClick={() => setEditingVideoForShot(shot)}
                                                    className="bg-cyan-600/50 hover:bg-cyan-600 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                    title="Edit video"
                                                >
                                                    ✏️ Edit Video
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => videoInputRefs.current[shot.id]?.click()}
                                                className="bg-gray-700 hover:bg-gray-600 text-white text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                            >
                                                <UploadIcon className="w-3 h-3" />
                                                Upload Video
                                            </button>
                                            {shot.video && (
                                                <button
                                                    onClick={() => handleShotUpdate(scene.id, shot.id, 'video', null)}
                                                    className="bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs py-2 rounded-md flex items-center justify-center gap-1"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                    Delete Video
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Add Scene Button */}
                <div className="w-[450px] flex-shrink-0">
                    <button
                        onClick={handleAddScene}
                        className="w-full h-32 border-2 border-dashed border-gray-700 hover:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-gray-400 transition-colors"
                    >
                        <PlusIcon className="w-8 h-8" />
                        <span className="font-semibold">Add Scene</span>
                    </button>
                </div>
            </div>

            {/* Modals */}
            {editingInfo && (
                <ImageEditorModal
                    shot={editingInfo.shot}
                    characters={projectData.characters}
                    projectData={projectData}
                    frameType={editingInfo.frame}
                    onClose={() => setEditingInfo(null)}
                    onSave={handleSaveFromImageEditor}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                    onSaveMedia={onSaveMedia}
                />
            )}

            {editingCharacter && (
                <CharacterPortraitModal
                    character={editingCharacter}
                    projectData={projectData}
                    onClose={() => {
                        setEditingCharacter(null);
                        setEditingCharacterForShot(null);
                    }}
                    onSave={(charId, saveData) => {
                        // Merge saved data with existing character
                        const updatedChar = {
                            ...editingCharacter,
                            ...saveData,
                            id: charId
                        };
                        
                        if (editingCharacterForShot) {
                            // New character being created from shot
                            handleSaveCharacter(updatedChar, editingCharacterForShot.sceneId, editingCharacterForShot.shotId);
                            setEditingCharacterForShot(null);
                        } else {
                            // Existing character being edited
                            const updatedChars = projectData.characters.map(c => c.id === charId ? updatedChar : c);
                            onProjectUpdate({ ...projectData, characters: updatedChars });
                            setEditingCharacter(null);
                        }
                    }}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                />
            )}

            {preppingVideoForShot && (
                <VideoPreProductionModal
                    shot={preppingVideoForShot}
                    characters={getCharactersForShot(preppingVideoForShot)}
                    projectData={projectData}
                    onClose={() => setPreppingVideoForShot(null)}
                    onSave={handleSaveFromPreProduction}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                    onUpdateCharacter={(updatedChar) => {
                        const updatedChars = projectData.characters.map(c => c.id === updatedChar.id ? updatedChar : c);
                        onProjectUpdate({ ...projectData, characters: updatedChars });
                    }}
                />
            )}

            {animatingShot && (
                <AnimateVideoModal
                    shot={animatingShot}
                    projectData={projectData}
                    onClose={() => setAnimatingShot(null)}
                    onSave={handleSaveFromAnimation}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                />
            )}

            {editingVideoForShot && (
                <VideoEditorModal
                    shot={editingVideoForShot}
                    projectData={projectData}
                    onClose={() => setEditingVideoForShot(null)}
                    onSave={(updatedShot) => {
                        const scene = scenes.find(s => s.shots?.some(sh => sh.id === updatedShot.id));
                        if (scene) {
                            handleShotUpdate(scene.id, updatedShot.id, 'video', updatedShot.video);
                        }
                        setEditingVideoForShot(null);
                    }}
                    onDeductCredits={onDeductCredits}
                />
            )}

            {previewingVideoUrl && (
                <VideoPreviewModal
                    videoUrl={previewingVideoUrl}
                    onClose={() => setPreviewingVideoUrl(null)}
                />
            )}

            {managingAudioForShot && (
                <ShotAudioModal
                    shot={managingAudioForShot}
                    onClose={() => setManagingAudioForShot(null)}
                    onDeductCredits={onDeductCredits}
                    onSave={(audio) => handleTranscribeAndSaveDescription(managingAudioForShot.id, audio)}
                />
            )}

            {/* Frame Image Viewing Modal */}
            {viewingFrameImage && (
                <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    onClick={() => setViewingFrameImage(null)}
                >
                    <div 
                        className="relative max-w-5xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-700">
                            <h3 className="text-lg font-semibold text-white">{viewingFrameImage.title || 'Frame Image'}</h3>
                            <button
                                onClick={() => setViewingFrameImage(null)}
                                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {/* Image */}
                        <div className="p-4 flex items-center justify-center bg-black">
                            <img 
                                src={viewingFrameImage.url} 
                                alt={viewingFrameImage.title || 'Frame'}
                                className="max-w-full max-h-[70vh] object-contain"
                            />
                        </div>
                        {/* Footer with download button */}
                        <div className="flex justify-center gap-4 p-4 border-t border-gray-700">
                            <a
                                href={viewingFrameImage.url}
                                download={`${viewingFrameImage.title || 'frame'}.png`}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                            </a>
                            <button
                                onClick={() => setViewingFrameImage(null)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Toolkit Settings Modal */}
            {showAIToolkitModal && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={() => setShowAIToolkitModal(false)}
                >
                    <div 
                        className="relative w-full max-w-6xl max-h-[90vh] bg-[#0f0f17] rounded-2xl overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <div>
                                <h3 className="text-2xl font-bold text-white">AI Model Settings</h3>
                                <p className="text-gray-400 text-sm mt-1">Change AI models for image/video generation. Changes apply to new generations only.</p>
                            </div>
                            <button
                                onClick={() => setShowAIToolkitModal(false)}
                                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <CloseIcon className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>
                        {/* AI Toolkit Selector */}
                        <div className="flex-1 overflow-y-auto">
                            <AIToolkitSelector 
                                onContinue={() => setShowAIToolkitModal(false)}
                                onBack={() => setShowAIToolkitModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
